import { objectSpread2 as _objectSpread2, defineProperty as _defineProperty } from '../_virtual/_rollupPluginBabelHelpers.js';
import fastMemoize from 'fast-memoize';
import { FontScales } from './scales.esm.js';
import { THEME_UI_DEFAULT_KEY } from '../theme-provider/types.esm.js';
import { memoizedGet } from '../utils/object.esm.js';
import { isSizeIndex, resolveSize } from '../utils/size-utils.esm.js';

var _checkbox, _checkbox2, _modal;

// use for fall back object and facilitate typing
var EMPTY_THEME = {};
var FLAT = '=flat=';
function isFlatDesign(theme) {
  return ((theme === null || theme === void 0 ? void 0 : theme.contextPath) || '').includes(FLAT);
}

// recommended approach to hide input but allow it to be read by screen reader.
// reference https://www.sarasoueidan.com/blog/inclusively-hiding-and-styling-checkboxes-and-radio-buttons/
var visuallyHiddenStyleForText = {
  clip: 'rect(0px, 0px, 0px, 0px)',
  clipPath: 'inset(100%)',
  border: '0px',
  height: '1px',
  width: '1px',
  margin: '-1px',
  padding: '0px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  /* remove element from flow */
  position: 'absolute'
};

// recommended approach to hide input but allow it to be read by screen reader.
// reference https://www.sarasoueidan.com/blog/inclusively-hiding-and-styling-checkboxes-and-radio-buttons/
var visuallyHiddenStyleForCheckboxOrRadio = {
  border: '0px',
  height: '1px',
  width: '1px',
  margin: '-1px',
  padding: '0px',
  /* remove element from flow */
  position: 'absolute',
  /* hide it visually */
  opacity: 0.00001
};
var boldTextFixLength = {
  '& span::before': {
    width: 'unset'
  },
  '& span::after': {
    display: 'block',
    width: 'unset',
    height: '0px',
    content: 'attr(content)',
    fontWeight: 'bold',
    color: 'transparent',
    visibility: 'hidden'
  }
};
var standardDisabled = {
  pointerEvents: 'unset',
  cursor: 'not-allowed',
  outline: 'unset',
  boxShadow: 'unset !important',
  textShadow: 'unset !important',
  opacity: 'unset'
};
var ellipsis = {
  display: 'block',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden'
};
var unsetLineHeight = {
  '&.unset-line-height': {
    lineHeight: 'unset'
  }
};
var rotateSvgCenterPoint = fastMemoize(function (degValue) {
  return {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    transform: "rotate(".concat(degValue, "deg)")
  };
});
var centerPosition = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
};
var fillContainer = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0
};
var animatedRotation = function animatedRotation() {
  var initialOrientation = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var rotate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 180;
  var enableTransition = arguments.length > 2 ? arguments[2] : undefined;
  return _objectSpread2(_objectSpread2({}, enableTransition ? {
    transition: 'transform .2s ease-in-out .05s'
  } : {}), {}, {
    transform: "rotate(".concat(initialOrientation, "deg)"),
    '&.is-toggle': {
      transform: "rotate(".concat(initialOrientation + rotate, "deg)")
    }
  });
};
var smallResponsiveValueSizes = [8, 9, 10, 11, 12];
var standardResponsiveValueSizes = [10, 11, 12, 13, 14];
var mediumResponsiveValueSizes = [12, 13, 14, 15, 16];
var largeResponsiveValueSizes = [14, 15, 16, 17, 18];
var xLargeResponsiveValueSizes = [16, 17, 18, 19, 20];
var xxLargeResponsiveValueSizes = [18, 19, 21, 20, 22];
var xSmallResponsiveSizes = ['xs-3', null, 'xs-2', null, 'xxs'];
var smallResponsiveSizes = ['xs-2', null, 'xxs', null, 'xs'];
var standardResponsiveSizes = ['xxs', null, 'xs', null, 'sm'];
var mediumResponsiveSizes = ['xs', null, 'sm', null, 'md'];
var largeResponsiveSizes = ['sm', null, 'md', null, 'lg'];
var xLargeResponsiveSizes = ['md', null, 'lg', null, 'xl'];
var xxLargeResponsiveSizes = ['lg', null, 'xl', null, 'xxl'];
var xSmallResponsiveSpace = ['xs-3', 'xs-2', null, 'xxs'];
var smallResponsiveSpace = ['xs-2', 'xxs', null, 'xs'];
var standardResponsiveSpace = ['xxs', null, 'xs', 'sm'];
var mediumResponsiveSpace = ['xs', 'sm', null, 'md'];
var largeResponsiveSpace = ['sm', 'md', null, 'lg'];
var xLargeResponsiveSpace = ['md', 'lg', null, 'xl'];
var ellipsisExpandResponsiveFontSizes = [FontScales['lp1-15'], FontScales['lp1-14'], FontScales['lp1-13'], FontScales['lp1-12'], FontScales['lp1-11'], FontScales['lp1-10']];
var h1ResponsiveFontSizes = [FontScales['lp2+2'],
//  28px 1.75rem
FontScales['lp2+3'],
// 30px 1.875rem
FontScales['lp2+4'],
// 32px 2rem
FontScales['lp2+5'],
// 34px 2.125rem
FontScales['lp2+6'],
// 36px 2.25rem
FontScales['lp2+7'] //  38px 2.375rem
];
var h2ResponsiveFontSizes = [FontScales['lp2+1'],
// 26px 1.625rem
FontScales['lp2+2'],
// 28px 1.75rem
FontScales['lp2+3'],
// 30px 1.875rem
FontScales['lp2+4'],
// 32px 2rem
FontScales['lp2+5'],
// 34px 2.125rem
FontScales['lp2+6'] //  36px 2.25rem
];
var h3ResponsiveFontSizes = [FontScales['base'],
//  24px 1.5rem
FontScales['lp2+1'],
// 26px 1.625rem
FontScales['lp2+2'],
// 28px 1.75rem
FontScales['lp2+3'],
// 30px 1.875rem
FontScales['lp2+4'],
// 32px 2rem
FontScales['lp2+5'] //  34px 2.125rem
];
var h4ResponsiveFontSizes = [FontScales['lp2-1'],
// 22px 1.375rem
FontScales['base'],
//  24px 1.5rem
FontScales['lp2+1'],
// 26px 1.625rem
FontScales['lp2+2'],
// 28px 1.75rem
FontScales['lp2+3'],
// 30px 1.875rem
FontScales['lp2+4'] //  32px 2rem
];
var h5ResponsiveFontSizes = [FontScales['lp2-2'],
// 20px 1.25rem
FontScales['lp2-1'],
// 22px 1.375rem
FontScales['base'],
//  24px 1.5rem
FontScales['lp2+1'],
// 26px 1.625rem
FontScales['lp2+2'],
// 28px 1.75rem
FontScales['lp2+3'] //  30px 1.875rem
];
var h6ResponsiveFontSizes = [FontScales['lp2-3'],
// 18px 1.125rem
FontScales['lp2-2'],
// 20px 1.25rem
FontScales['lp2-1'],
// 22px 1.375rem
FontScales['base'],
//  24px 1.5rem
FontScales['lp2+1'],
// 26px 1.625rem
FontScales['lp2+2'] //  28px 1.75rem
];
var zIndices = {
  mobileStepper: 1000,
  speedDial: 1050,
  appBar: 1100,
  drawer: 1200,
  toolbar: 1201,
  selectPopover: 1202,
  selectButton: 1203,
  selectOverlapPopover: 1204,
  checkbox: (_checkbox = {}, _defineProperty(_checkbox, THEME_UI_DEFAULT_KEY, 1205), _defineProperty(_checkbox, "plus_one", 1206), _checkbox),
  sticky: 1299,
  snackbar: 1400,
  tooltip: 10004,
  //1500
  masthead: 4999,
  // from costar suite
  // To work in conjunction with modal manager we want establish a new base zIndex for each
  // modal being create. TBD it may be necessary to make this more dynamic.
  // see src\components\_utils\modal\modal.tsx line # 213
  modal: (_modal = {}, _defineProperty(_modal, THEME_UI_DEFAULT_KEY, 10000), _defineProperty(_modal, "selectPopover", 10003), _defineProperty(_modal, "selectOverlapPopover", 10005), _defineProperty(_modal, "checkbox", (_checkbox2 = {}, _defineProperty(_checkbox2, THEME_UI_DEFAULT_KEY, 10020), _defineProperty(_checkbox2, "plus_one", 10021), _checkbox2)), _defineProperty(_modal, "sticky", 10099), _defineProperty(_modal, "mobileStepper", 11000), _modal)
};
var responsiveImageHeight = [100, 110, 120, 130, 140];
var responsiveImageWidth = responsiveImageHeight.map(function (i) {
  return i * 1.4375;
});
var calculatedOffsets = function calculatedOffsets(offsets) {
  var theme = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var percentValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 100;
  var _theme$space = theme.space,
    space = _theme$space === void 0 ? [] : _theme$space;
  if (theme) {
    if (Array.isArray(offsets)) {
      if (offsets.some(function (i) {
        return isSizeIndex(i);
      })) {
        var spaceSizes = resolveSize(offsets, memoizedGet(theme, 'sizes.space'));
        return spaceSizes.map(function (i) {
          return "calc(".concat(percentValue, "% - ").concat(memoizedGet(space, i), ")");
        });
      }
    } else {
      if (isSizeIndex(offsets)) {
        var _spaceSizes = resolveSize(offsets, memoizedGet(theme, 'sizes.space'));
        return "calc(".concat(percentValue, "% - ").concat(memoizedGet(space, _spaceSizes), ")");
      }
    }
  }
  return Array.isArray(offsets) ? offsets.map(function (i) {
    return "calc(".concat(percentValue, "% - ").concat(i, "px)");
  }) : "calc(".concat(percentValue, "% - ").concat(offsets, "px)");
};
var titleTooltipProps = {
  variant: 'title',
  interactive: true,
  disableFocusListener: true,
  placement: 'bottom-start',
  PopperProps: {
    modifiers: {
      offset: {
        enabled: true,
        offset: '50,-10'
      }
    }
  }
};

export { EMPTY_THEME, FLAT, animatedRotation, boldTextFixLength, calculatedOffsets, centerPosition, ellipsis, ellipsisExpandResponsiveFontSizes, fillContainer, h1ResponsiveFontSizes, h2ResponsiveFontSizes, h3ResponsiveFontSizes, h4ResponsiveFontSizes, h5ResponsiveFontSizes, h6ResponsiveFontSizes, isFlatDesign, largeResponsiveSizes, largeResponsiveSpace, largeResponsiveValueSizes, mediumResponsiveSizes, mediumResponsiveSpace, mediumResponsiveValueSizes, responsiveImageHeight, responsiveImageWidth, rotateSvgCenterPoint, smallResponsiveSizes, smallResponsiveSpace, smallResponsiveValueSizes, standardDisabled, standardResponsiveSizes, standardResponsiveSpace, standardResponsiveValueSizes, titleTooltipProps, unsetLineHeight, visuallyHiddenStyleForCheckboxOrRadio, visuallyHiddenStyleForText, xLargeResponsiveSizes, xLargeResponsiveSpace, xLargeResponsiveValueSizes, xSmallResponsiveSizes, xSmallResponsiveSpace, xxLargeResponsiveSizes, xxLargeResponsiveValueSizes, zIndices };
//# sourceMappingURL=const.esm.js.map
